import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCode, faLightbulb, faMicrochip, faFlask, faArrowRight,
  faBookOpen, faVial, faCubes, faTrophy, faLanguage,
} from '@fortawesome/free-solid-svg-icons';
import { CATEGORIES, COURSES, PATHS, getCourse } from '../data/courses';
import './Curriculum.css';

// What every course builds over time
const PILLARS = [
  { icon: faCode, title: 'Coding foundations', text: 'Apps, games and websites, plus the algorithmic thinking behind them.' },
  { icon: faLightbulb, title: 'Creative thinking', text: 'Design thinking and real-life projects that start from the child’s own ideas.' },
  { icon: faMicrochip, title: 'Advanced tech', text: 'A solid base in AI, machine learning, VR and robotics.' },
  { icon: faFlask, title: 'Science & math', text: 'Coding that strengthens scientific and mathematical skills.' },
];

// How every session is built
const SESSION_STEPS = [
  { icon: faBookOpen, title: 'Concept', text: 'A new idea, explained simply with examples.' },
  { icon: faVial, title: 'Experiment', text: 'Your child tries it hands-on, with the instructor guiding live.' },
  { icon: faCubes, title: 'Project', text: 'They apply it to build something of their own.' },
  { icon: faTrophy, title: 'Result', text: 'Every session ends with work to save and show.' },
];

// Tools used in the courses (for parents who want to know)
const TOOLS = {
  'game-dev': 'Scratch · Code.org',
  'mobile-apps': 'Code.org · Thunkable',
  'web-intro': 'HTML · CSS',
  'web-advanced': 'HTML · CSS · JavaScript',
  'web-mastery': 'PHP · MySQL',
  'python-intro': 'Python',
  'python-advanced': 'Python',
  'ai-python': 'Python · AI',
  vr: 'CoSpaces',
  design: 'Tinkercad',
  arduino: 'Arduino',
  microbit: 'Micro:bit',
  'vex-vr': 'VEXcode VR',
  cpp: 'C++',
  java: 'Java',
};

// Courses open to 6–8 year olds = courses whose age range starts at 6
const youngCourses = COURSES.filter((c) => c.age.startsWith('6'));
const olderOnlyCourses = COURSES.filter((c) => !c.age.startsWith('6'));

const CourseChip = ({ course }) => {
  const cat = CATEGORIES[course.category];
  return (
    <li className="rmcur-chip">
      <span className="rmcur-dot" style={{ background: cat.color }} aria-hidden="true" />
      <span className="rmcur-chip-body">
        <span className="rmcur-chip-name">
          {course.title}
          {course.isNew && <span className="rmcur-new">NEW</span>}
        </span>
        {TOOLS[course.id] && <span className="rmcur-chip-tools">{TOOLS[course.id]}</span>}
      </span>
    </li>
  );
};

const CurriculumOverview = () => {
  return (
    <section className="rmcur" aria-labelledby="rmcur-title">
      <div className="rmcur-inner">
        {/* Header */}
        <header className="rmcur-header">
          <span className="rmcur-eyebrow">Curriculum</span>
          <h1 id="rmcur-title" className="rmcur-title">
            How your child <span>learns with us</span>
          </h1>
          <p className="rmcur-sub">
            A structured, project-based curriculum for ages 6–17, taught live and 1-to-1 by an instructor who adapts
            to your child’s pace.
          </p>
          <div className="rmcur-lang">
            <FontAwesomeIcon icon={faLanguage} />
            Classes in <strong>Arabic</strong>, <strong>French</strong> or <strong>English</strong>
          </div>
        </header>

        {/* Pillars */}
        <div className="rmcur-block">
          <h2 className="rmcur-h2">What every child builds</h2>
          <div className="rmcur-pillars">
            {PILLARS.map((p) => (
              <div key={p.title} className="rmcur-pillar">
                <span className="rmcur-pillar-icon">
                  <FontAwesomeIcon icon={p.icon} />
                </span>
                <h3>{p.title}</h3>
                <p>{p.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Session structure */}
        <div className="rmcur-block">
          <h2 className="rmcur-h2">Inside every session</h2>
          <ol className="rmcur-session">
            {SESSION_STEPS.map((s, i) => (
              <li key={s.title} className="rmcur-session-step">
                <span className="rmcur-session-num" aria-hidden="true">
                  {i + 1}
                </span>
                <div>
                  <h3>
                    <FontAwesomeIcon icon={s.icon} /> {s.title}
                  </h3>
                  <p>{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* By age */}
        <div className="rmcur-block">
          <h2 className="rmcur-h2">Where to start, by age</h2>
          <p className="rmcur-block-sub">
            Age is a guide, not a rule. Experience matters too, so we recommend the right start after the free trial.
          </p>
          <div className="rmcur-ages">
            <div className="rmcur-age">
              <div className="rmcur-age-head">
                <span className="rmcur-age-badge">Ages 6–8</span>
                <span className="rmcur-age-note">Visual, playful first steps</span>
              </div>
              <ul className="rmcur-chips">
                {youngCourses.map((c) => (
                  <CourseChip key={c.id} course={c} />
                ))}
              </ul>
            </div>
            <div className="rmcur-age">
              <div className="rmcur-age-head">
                <span className="rmcur-age-badge rmcur-age-badge--older">Ages 9–17</span>
                <span className="rmcur-age-note">All courses above, plus</span>
              </div>
              <ul className="rmcur-chips">
                {olderOnlyCourses.map((c) => (
                  <CourseChip key={c.id} course={c} />
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Paths */}
        <div className="rmcur-block">
          <h2 className="rmcur-h2">Grow step by step</h2>
          <div className="rmcur-paths">
            {PATHS.map((path) => (
              <div key={path.id} className="rmcur-path">
                <h3>{path.title}</h3>
                <ol>
                  {path.steps.map((id, i) => {
                    const c = getCourse(id);
                    if (!c) return null;
                    return (
                      <li key={id}>
                        <span className="rmcur-path-num">{i + 1}</span>
                        <span className="rmcur-path-name">{c.title}</span>
                        <span className="rmcur-path-meta">{c.sessions} sessions</span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ))}
          </div>
        </div>

        <Link to="/Programs" className="rmcur-link">
          See all {COURSES.length} courses
          <FontAwesomeIcon icon={faArrowRight} />
        </Link>
      </div>
    </section>
  );
};

export default CurriculumOverview;